import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import {
    createMeetingRequest,
    getMeetingRequestCalendars,
    getMeetingRequestSettings,
    getMeetingRequests,
    getUsers,
    updateMeetingRequestSettings,
} from "./api";

const defaultSettings = {
    timezone: "Europe/London",
    available_weekdays: [1, 2, 3, 4, 5],
    day_start_time: "09:00",
    day_end_time: "17:00",
    slot_minutes: 30,
    buffer_minutes: 0,
    max_calls_per_day: 6,
    min_notice_hours: 24,
    max_days_ahead: 60,
    outlook_calendar_user_id: "",
    outlook_calendar_id: "",
    booking_redirect_url: "",
    booking_redirect_include_params: false,
};

const emptyCreateForm = {
    title: "",
    description: "",
    contact_user_id: "",
    duration_minutes: 30,
};

function formatDateTime(value, timezone = "Europe/London") {
    if (!value) return "Not set";
    const raw = String(value).trim();
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
    const text = raw.replace(" ", "T");
    const dt = hasTimezone ? new Date(text) : new Date(`${text}Z`);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: timezone,
    });
}

function formatStatus(status) {
    if (status === "requested") return "Requested";
    if (status === "scheduled") return "Scheduled";
    if (status === "cancelled") return "Cancelled";
    return status || "Unknown";
}

function statusPillClass(status) {
    if (status === "scheduled") return "pill--lead-scheduled";
    if (status === "cancelled") return "pill--lead-cancelled";
    return "pill--review-pending";
}

function MeetingRequests({ onLogout, embedded = false }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [createForm, setCreateForm] = useState(emptyCreateForm);
    const [createError, setCreateError] = useState("");
    const [creating, setCreating] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [createdPublicLink, setCreatedPublicLink] = useState("");

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsError, setSettingsError] = useState("");
    const [settingsForm, setSettingsForm] = useState(defaultSettings);

    const [calendarOptions, setCalendarOptions] = useState([]);
    const [calendarsLoading, setCalendarsLoading] = useState(false);

    const [copiedRequestId, setCopiedRequestId] = useState(null);

    const count = useMemo(() => requests.length, [requests]);

    const loadRequests = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const rows = await getMeetingRequests();
            setRequests(rows);
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load meeting requests");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadSettings = useCallback(async () => {
        setSettingsLoading(true);
        setSettingsError("");
        try {
            const settings = await getMeetingRequestSettings();
            setSettingsForm({
                ...defaultSettings,
                ...settings,
                available_weekdays:
                    settings?.available_weekdays || defaultSettings.available_weekdays,
            });

            if (settings?.outlook_calendar_user_id) {
                const calendars = await getMeetingRequestCalendars(
                    settings.outlook_calendar_user_id
                );
                setCalendarOptions(calendars);
            } else {
                setCalendarOptions([]);
            }
        } catch (err) {
            console.error(err);
            setSettingsError(err.message || "Failed to load settings");
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    useEffect(() => {
        async function loadUsers() {
            setUsersLoading(true);
            try {
                const list = await getUsers();
                setUsers(Array.isArray(list) ? list : []);
            } catch (err) {
                console.error(err);
                setCreateError(err.message || "Failed to load users");
            } finally {
                setUsersLoading(false);
            }
        }
        loadUsers();
    }, []);

    function toggleWeekday(day) {
        setSettingsForm((prev) => {
            const current = new Set(prev.available_weekdays || []);
            if (current.has(day)) current.delete(day);
            else current.add(day);
            return {
                ...prev,
                available_weekdays: [...current].sort((a, b) => a - b),
            };
        });
    }

    async function handleLoadCalendars() {
        const mailbox = String(settingsForm.outlook_calendar_user_id || "").trim();
        if (!mailbox) {
            setSettingsError("Enter an Outlook mailbox/user first");
            return;
        }

        setCalendarsLoading(true);
        setSettingsError("");
        try {
            const calendars = await getMeetingRequestCalendars(mailbox);
            setCalendarOptions(calendars);
        } catch (err) {
            console.error(err);
            setSettingsError(err.message || "Failed to load calendars");
        } finally {
            setCalendarsLoading(false);
        }
    }

    async function handleSaveSettings(e) {
        e.preventDefault();
        setSettingsSaving(true);
        setSettingsError("");
        try {
            const saved = await updateMeetingRequestSettings(settingsForm);
            setSettingsForm({
                ...defaultSettings,
                ...saved,
                available_weekdays:
                    saved?.available_weekdays || defaultSettings.available_weekdays,
            });
            setSettingsOpen(false);
        } catch (err) {
            console.error(err);
            setSettingsError(err.message || "Failed to save settings");
        } finally {
            setSettingsSaving(false);
        }
    }

    async function handleCreate(e) {
        e.preventDefault();
        setCreateError("");
        setCreatedPublicLink("");

        if (!String(createForm.title || "").trim()) {
            setCreateError("Title is required");
            return;
        }

        const contactUserId = Number.parseInt(
            String(createForm.contact_user_id || ""),
            10
        );
        if (!Number.isInteger(contactUserId) || contactUserId <= 0) {
            setCreateError("Select a contact user");
            return;
        }

        setCreating(true);
        try {
            const created = await createMeetingRequest({
                ...createForm,
                contact_user_id: contactUserId,
            });

            const publicUrl = String(created?.public_url || "").trim();
            const publicPath = String(created?.public_path || "").trim();
            if (publicUrl) {
                setCreatedPublicLink(publicUrl);
            } else if (publicPath) {
                const base = window.location.origin.replace(/\/$/, "");
                const normalizedPath = publicPath.startsWith("/")
                    ? publicPath
                    : `/${publicPath}`;
                setCreatedPublicLink(`${base}${normalizedPath}`);
            }

            setCreateForm(emptyCreateForm);
            await loadRequests();
        } catch (err) {
            console.error(err);
            setCreateError(err.message || "Failed to create meeting request");
        } finally {
            setCreating(false);
        }
    }

    const selectedContactUser = useMemo(() => {
        const id = Number.parseInt(String(createForm.contact_user_id || ""), 10);
        if (!Number.isInteger(id) || id <= 0) return null;
        return users.find((user) => Number(user.id) === id) || null;
    }, [createForm.contact_user_id, users]);

    async function handleCopyLink(token, requestId) {
        const url = `${window.location.origin}/#/meeting-request?token=${encodeURIComponent(
            token
        )}`;

        try {
            await navigator.clipboard.writeText(url);
            setCopiedRequestId(requestId);
            window.setTimeout(() => {
                setCopiedRequestId((current) =>
                    current === requestId ? null : current
                );
            }, 1500);
        } catch {
            setCreateError("Could not copy link. Please copy manually from row details.");
        }
    }

    return (
        <PageShell onLogout={onLogout} embedded={embedded}>
            <section className="portal-page">
                <header className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">Teams Meetings</h2>
                        <p className="portal-page__subtitle">
                            Create scheduling links and manage meeting settings.
                        </p>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button
                            type="button"
                            className="btn btn--secondary"
                            onClick={async () => {
                                setSettingsOpen(true);
                                await loadSettings();
                            }}
                        >
                            Settings
                        </button>
                        <button
                            type="button"
                            className="btn btn--secondary"
                            onClick={loadRequests}
                        >
                            Refresh
                        </button>
                    </div>
                </header>

                {error && <div className="alert alert--error">{error}</div>}

                <div className="card" style={{ marginBottom: 14 }}>
                    <h3 style={{ marginTop: 0 }}>Create Meeting Request</h3>
                    {createError ? <div className="alert alert--error">{createError}</div> : null}
                    {createdPublicLink ? (
                        <div className="alert alert--success">
                            <div style={{ marginBottom: 8 }}>
                                Scheduling link created. Copy and send this manually:
                            </div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <input
                                    className="form-field__input"
                                    value={createdPublicLink}
                                    readOnly
                                />
                                <button
                                    type="button"
                                    className="btn btn--secondary"
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(createdPublicLink);
                                        } catch {
                                            setCreateError("Could not copy link. Please copy it manually.");
                                        }
                                    }}
                                >
                                    Copy
                                </button>
                            </div>
                        </div>
                    ) : null}
                    <form onSubmit={handleCreate}>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <label className="form-field" style={{ flex: "1 1 280px" }}>
                                <span className="form-field__label">Title</span>
                                <input
                                    className="form-field__input"
                                    value={createForm.title}
                                    onChange={(e) =>
                                        setCreateForm((prev) => ({
                                            ...prev,
                                            title: e.target.value,
                                        }))
                                    }
                                    required
                                />
                            </label>
                            <label className="form-field" style={{ flex: "1 1 280px" }}>
                                <span className="form-field__label">Contact user</span>
                                <select
                                    className="form-field__select"
                                    value={createForm.contact_user_id}
                                    onChange={(e) =>
                                        setCreateForm((prev) => ({
                                            ...prev,
                                            contact_user_id: e.target.value,
                                        }))
                                    }
                                    required
                                    disabled={usersLoading}
                                >
                                    <option value="">
                                        {usersLoading ? "Loading users..." : "Select user..."}
                                    </option>
                                    {users.map((user) => {
                                        const firstName = String(user.first_name || "").trim();
                                        const surname = String(user.surname || "").trim();
                                        const name =
                                            [firstName, surname]
                                                .filter(Boolean)
                                                .join(" ")
                                                .trim() ||
                                            String(user.name || "").trim() ||
                                            String(user.email || "").trim();
                                        const email = String(user.email || "").trim();
                                        return (
                                            <option key={user.id} value={String(user.id)}>
                                                {name}
                                                {email ? ` (${email})` : ""}
                                            </option>
                                        );
                                    })}
                                </select>
                            </label>
                        </div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            {selectedContactUser ? (
                                <label className="form-field" style={{ flex: "1 1 280px" }}>
                                    <span className="form-field__label">Contact email</span>
                                    <input
                                        className="form-field__input"
                                        value={String(selectedContactUser.email || "")}
                                        disabled
                                    />
                                </label>
                            ) : null}
                            <label className="form-field" style={{ flex: "1 1 180px" }}>
                                <span className="form-field__label">Duration (minutes)</span>
                                <select
                                    className="form-field__select"
                                    value={createForm.duration_minutes}
                                    onChange={(e) =>
                                        setCreateForm((prev) => ({
                                            ...prev,
                                            duration_minutes: Number(e.target.value),
                                        }))
                                    }
                                >
                                    <option value={15}>15</option>
                                    <option value={30}>30</option>
                                    <option value={60}>60</option>
                                </select>
                            </label>
                        </div>

                        <label className="form-field">
                            <span className="form-field__label">Description</span>
                            <textarea
                                className="form-field__textarea"
                                rows={3}
                                value={createForm.description}
                                onChange={(e) =>
                                    setCreateForm((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }))
                                }
                            />
                        </label>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button className="btn btn--primary" type="submit" disabled={creating}>
                                {creating ? "Creating..." : "Create Request"}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="card">
                    <h3 style={{ marginTop: 0 }}>Requests ({count})</h3>
                    {loading ? <p>Loading meeting requests...</p> : null}
                    {!loading && requests.length === 0 ? (
                        <p>No meeting requests yet.</p>
                    ) : null}

                    {!loading && requests.length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                            <table className="portal-table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Status</th>
                                        <th>Contact</th>
                                        <th>Scheduled</th>
                                        <th>Link</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.map((row) => (
                                        <tr key={row.id}>
                                            <td>
                                                <strong>{row.title}</strong>
                                                {row.description ? (
                                                    <div className="table-subtext">{row.description}</div>
                                                ) : null}
                                            </td>
                                            <td>
                                                <span className={`pill ${statusPillClass(row.status)}`}>
                                                    {formatStatus(row.status)}
                                                </span>
                                            </td>
                                            <td>
                                                <div>{row.contact_name || "-"}</div>
                                                <div className="table-subtext">{row.contact_email}</div>
                                            </td>
                                            <td>
                                                {formatDateTime(
                                                    row.scheduled_at,
                                                    settingsForm.timezone || "Europe/London"
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn--secondary"
                                                    onClick={() => handleCopyLink(row.token, row.id)}
                                                >
                                                    {copiedRequestId === row.id ? "Copied" : "Copy Link"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>
            </section>

            {settingsOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Teams Meeting Settings</h3>
                                <p className="modal__subtitle">
                                    Configure meeting booking availability.
                                </p>
                            </div>
                            <button
                                className="modal__close-btn"
                                onClick={() => setSettingsOpen(false)}
                            >
                                ✕
                            </button>
                        </div>

                        {settingsError ? (
                            <div className="alert alert--error">{settingsError}</div>
                        ) : null}

                        {settingsLoading ? (
                            <div className="modal__body">
                                <p>Loading settings...</p>
                            </div>
                        ) : (
                            <form className="modal__body" onSubmit={handleSaveSettings}>
                                <fieldset className="fieldset">
                                    <legend className="fieldset__legend">Availability</legend>

                                    <label className="form-field">
                                        <span className="form-field__label">Timezone</span>
                                        <input
                                            className="form-field__input"
                                            value={settingsForm.timezone}
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    timezone: e.target.value,
                                                }))
                                            }
                                        />
                                    </label>

                                    <label className="form-field">
                                        <span className="form-field__label">
                                            Outlook mailbox/user
                                        </span>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: 8,
                                                alignItems: "center",
                                            }}
                                        >
                                            <input
                                                className="form-field__input"
                                                value={settingsForm.outlook_calendar_user_id || ""}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        outlook_calendar_user_id: e.target.value,
                                                    }))
                                                }
                                                placeholder="mailbox@yourdomain.com"
                                            />
                                            <button
                                                type="button"
                                                className="btn btn--secondary"
                                                onClick={handleLoadCalendars}
                                                disabled={calendarsLoading}
                                            >
                                                {calendarsLoading ? "Loading..." : "Load calendars"}
                                            </button>
                                        </div>
                                    </label>

                                    <label className="form-field">
                                        <span className="form-field__label">Outlook calendar</span>
                                        <select
                                            className="form-field__select"
                                            value={settingsForm.outlook_calendar_id || ""}
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    outlook_calendar_id: e.target.value,
                                                }))
                                            }
                                        >
                                            <option value="">Default calendar</option>
                                            {calendarOptions.map((cal) => (
                                                <option key={cal.id} value={cal.id}>
                                                    {cal.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="form-field">
                                        <span className="form-field__label">
                                            Booking confirmation redirect URL
                                        </span>
                                        <input
                                            className="form-field__input"
                                            value={settingsForm.booking_redirect_url || ""}
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    booking_redirect_url: e.target.value,
                                                }))
                                            }
                                            placeholder="https://example.com/thank-you"
                                        />
                                    </label>

                                    <label
                                        className="form-field"
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: 10,
                                            alignItems: "center",
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={Boolean(
                                                settingsForm.booking_redirect_include_params
                                            )}
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    booking_redirect_include_params:
                                                        e.target.checked,
                                                }))
                                            }
                                            style={{ margin: 0, flex: "0 0 auto" }}
                                        />
                                        <span className="form-field__label" style={{ margin: 0 }}>
                                            Include query params (`?email=...&name=...`) on
                                            booking redirect
                                        </span>
                                    </label>

                                    <div className="form-field">
                                        <span className="form-field__label">Available weekdays</span>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            {[
                                                { day: 1, label: "Mon" },
                                                { day: 2, label: "Tue" },
                                                { day: 3, label: "Wed" },
                                                { day: 4, label: "Thu" },
                                                { day: 5, label: "Fri" },
                                                { day: 6, label: "Sat" },
                                                { day: 0, label: "Sun" },
                                            ].map((opt) => (
                                                <button
                                                    key={opt.day}
                                                    type="button"
                                                    className={
                                                        "btn " +
                                                        (settingsForm.available_weekdays?.includes(
                                                            opt.day
                                                        )
                                                            ? "btn--primary"
                                                            : "btn--secondary")
                                                    }
                                                    onClick={() => toggleWeekday(opt.day)}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Start time</span>
                                            <input
                                                type="time"
                                                className="form-field__input"
                                                value={settingsForm.day_start_time}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        day_start_time: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">End time</span>
                                            <input
                                                type="time"
                                                className="form-field__input"
                                                value={settingsForm.day_end_time}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        day_end_time: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Slot minutes</span>
                                            <select
                                                className="form-field__select"
                                                value={settingsForm.slot_minutes}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        slot_minutes: Number(e.target.value),
                                                    }))
                                                }
                                            >
                                                <option value={15}>15</option>
                                                <option value={30}>30</option>
                                                <option value={60}>60</option>
                                            </select>
                                        </label>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Buffer (minutes)</span>
                                            <input
                                                type="number"
                                                min={0}
                                                max={120}
                                                className="form-field__input"
                                                value={settingsForm.buffer_minutes}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        buffer_minutes: Number(e.target.value),
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Max calls/day</span>
                                            <input
                                                type="number"
                                                className="form-field__input"
                                                value={settingsForm.max_calls_per_day}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        max_calls_per_day: Number(e.target.value),
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Min notice (hours)</span>
                                            <input
                                                type="number"
                                                className="form-field__input"
                                                value={settingsForm.min_notice_hours}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        min_notice_hours: Number(e.target.value),
                                                    }))
                                                }
                                            />
                                        </label>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Max days ahead</span>
                                            <input
                                                type="number"
                                                className="form-field__input"
                                                value={settingsForm.max_days_ahead}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        max_days_ahead: Number(e.target.value),
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>
                                </fieldset>

                                <div className="modal__footer">
                                    <span />
                                    <div style={{ display: "flex", gap: 10 }}>
                                        <button
                                            type="button"
                                            className="btn btn--secondary"
                                            onClick={() => setSettingsOpen(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn--primary"
                                            disabled={settingsSaving}
                                        >
                                            {settingsSaving ? "Saving..." : "Save Settings"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </PageShell>
    );
}

export default MeetingRequests;
